import { RED, cell, dmy, money, phone, stamp } from "@/components/quote/docKit";

const ROWS = ["CPU", "MOTHERBOARD", "COOLER", "RAM", "GRAPHICS_CARD", "SSD1", "SSD2", "HDD", "CHASSIS", "PSU", "HEADPHONES", "MOUSE", "KEYBOARD", "MONITOR", "FANS"];
const NAMES = { CPU: "CPU", MOTHERBOARD: "Motherboard", COOLER: "Cooler", RAM: "RAM", GRAPHICS_CARD: "Graphics Card", SSD1: "SSD1", SSD2: "SSD2", HDD: "HDD",
  CHASSIS: "Chassis", PSU: "PSU", HEADPHONES: "Headphones", MOUSE: "Mouse", KEYBOARD: "Keyboard", MONITOR: "Monitor", FANS: "Fans", CUSTOM_ACCESSORY: "Accessory" };
const YEARS = { "1Y": "1 Year", "2Y": "2 Years", "3Y": "3 Years" };
const GREEN = "#3fd45a";

export function DocTopLine({ d }) {
  return (
    <div className="flex justify-between text-[10px] text-zinc-600 mb-2">
      <span data-testid="quote-generated-at">{stamp(new Date())}&nbsp;&nbsp;&nbsp;1</span>
      <span data-testid="quote-tracking">GN PC Quotation {d.fyLabel} {d.salesRepCode} {d.quarter} Rev-{d.versionNumber} {d.quoteNo}</span>
    </div>
  );
}

export function QuoteHeader({ d, t }) {
  const created = new Date(d.createdAt);
  const expiry = new Date(created.getTime() + (d.validityDays || t.validityDays) * 864e5);
  const rows = [["Quote Date", dmy(created), "quote-date"], ["Quotation Expiry Date", dmy(expiry), "quote-expiry"], ["Quote No:", d.quoteNo, "quote-no"]];
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start p-4">
      <div><img src="/brand/gn-logo.png" alt="Gamerz Nation" className="h-[72px] w-auto" />
        <div className="mt-2 text-[10px] leading-snug whitespace-pre-line text-zinc-700">{t.companyAddress}{"\n"}{t.companyPhone}</div></div>
      <div className="text-2xl font-bold pt-5" style={{ color: RED }}>Quotation</div>
      <div className="justify-self-end w-60 text-[11px] text-white" style={{ background: RED }} data-testid="quote-header-box">
        {rows.map(([k, v, id]) => (
          <div key={k} className="grid grid-cols-2 border-b border-white/40"><span className="px-2 py-1 border-r border-white/40">{k}</span>
            <span className="px-2 py-1 text-right font-semibold" data-testid={id}>{v}</span></div>))}
        <div className="px-2 py-2 text-center"><div>Customer Name:</div><div className="font-semibold" data-testid="quote-customer-name">{d.client.name}</div>
          <div className="mt-1.5">Customer Number:</div><div className="font-semibold" data-testid="quote-customer-number">{phone(d.client.phone)}</div></div>
      </div>
    </div>
  );
}

function Row({ desc, name, qty, price, showPrice, free, testid }) {
  const td = "px-2 py-[3px] border";
  return (
    <tr style={free ? { background: GREEN } : undefined} data-testid={testid}>
      <td className={`${td} w-[140px]`} style={{ ...cell, color: free ? "#b71c1c" : undefined }}>{desc}</td>
      <td className={`${td} text-center`} style={{ ...cell, color: free ? "#b71c1c" : undefined }}>{name}</td>
      <td className={`${td} w-12 text-right font-semibold`} style={{ ...cell, color: free ? "#b71c1c" : undefined }}>{qty}</td>
      {showPrice && <td className={`${td} w-28 text-right`} style={cell}>{price != null ? money(price) : ""}</td>}
    </tr>
  );
}

export function BomTable({ d, t }) {
  const show = !d.hideLineItemCost;
  const byCat = (c) => d.items.filter((i) => i.category === c);
  const extra = d.items.filter((i) => !ROWS.includes(i.category));
  const lines = ROWS.flatMap((c) => (byCat(c).length ? byCat(c) : [{ category: c, blank: true }])).concat(extra);
  return (
    <table className="w-full text-[11px] border-collapse" data-testid="quote-bom-table">
      <thead><tr style={{ color: RED }} className="font-semibold text-[13px]">
        <th className="px-2 py-1 border text-left" style={cell}>Description</th><th className="px-2 py-1 border" style={cell}>Part name</th>
        <th className="px-2 py-1 border text-right" style={cell}>Qty</th>{show && <th className="px-2 py-1 border text-right" style={cell}>Taxable Price</th>}</tr></thead>
      <tbody>
        {lines.map((i, k) => <Row key={k} desc={NAMES[i.category] || i.category} name={i.blank ? "" : i.name} qty={i.blank ? "" : 1}
          price={i.blank ? null : i.taxableValue} showPrice={show} testid={`bom-row-${i.category}`} />)}
        {d.amcPlan && <Row desc="GN Privilege" name={`GN PRIVILEGE PACKAGE ${YEARS[d.amcPlan].toUpperCase()}`} qty={1}
          price={d.amcAmount} showPrice={show} testid="bom-row-privilege" />}
        {t.freeInclusionLabel && <Row free desc={t.freeInclusionLabel} name={t.freeInclusionValue} qty="FREE" showPrice={show} testid="bom-row-free-inclusion" />}
        {d.eligibleGifts.map((g) => <Row key={g} free desc="Free Gift" name={g} qty="FREE" showPrice={show} testid="bom-row-free-gift" />)}
      </tbody>
    </table>
  );
}

export function Summary({ d, t }) {
  const line = "grid grid-cols-[1fr_90px_130px] text-[12px]";
  return (
    <div className="mt-1" data-testid="quote-summary">
      <div className={line}><span /><span className="px-2 py-1 border text-right" style={cell}>Subtotal</span>
        <span className="px-2 py-1 border text-right" style={cell} data-testid="quote-subtotal">{money(d.totalTaxableValue + d.amcAmount)}</span></div>
      <div className={line}><span className="px-2 py-1 text-[11px] text-right" style={{ color: RED }}>{t.gstNote}</span>
        <span className="px-2 py-1 border text-right" style={cell}>GST 18%</span>
        <span className="px-2 py-1 border text-right" style={cell} data-testid="quote-gst">{money(d.totalGstAmount + d.amcGstAmount)}</span></div>
      <div className={line}><span /><span className="px-2 py-1 text-right text-[13px] font-bold whitespace-nowrap">Total Payable</span>
        <span className="px-2 py-1 border text-right text-[15px] font-bold" style={cell} data-testid="quote-total">{money(d.grandTotal)}</span></div>
    </div>
  );
}
